import MuiDrawer from '@mui/material/Drawer';
import { Theme } from '@mui/material/styles/createTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import React from 'react';

import { Box } from './Box';
import { SafeArea } from './SafeArea';
import { TitleAndClose } from './TitleAndClose';

export interface DrawerLayoutProps {
  drawerOpen: boolean;
  drawer?: React.ReactNode;
  drawerWidth?: string; // PX value
  children: React.ReactNode;
}

export const DrawerLayout: React.FC<DrawerLayoutProps> = ({
  children,
  drawerOpen,
  drawerWidth = '450px',
}) => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const drawerShouldTakeSpace = !mdDown && drawerOpen;

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'flex-start',
        flex: '1 1 auto',
        flexDirection: 'column',
        zIndex: 1,
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: drawerOpen
              ? theme.transitions.easing.easeOut
              : theme.transitions.easing.sharp,
            duration: drawerOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
        width: drawerShouldTakeSpace ? `calc(100% - ${drawerWidth})` : '100%',
        position: 'relative',
      }}
    >
      <Box sx={{ width: '100%' }}>{children}</Box>
    </Box>
  );
};

export interface DrawerProps {
  open: boolean;
  width?: string; // PX value
  onClose?: () => void;
  title?: string;
  leftAdornment?: React.ReactNode;
  rightAdornment?: React.ReactNode;
  titleVariant?: 'h5' | 'h6';
  children: React.ReactNode;
}

export const Drawer: React.FC<DrawerProps> = ({
  children,
  open,
  onClose,
  width = '450px',
  title,
  leftAdornment,
  rightAdornment,
  titleVariant = 'h5',
}) => {
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <MuiDrawer
      anchor="right"
      variant={mdDown ? 'temporary' : 'persistent'}
      sx={{
        '& .MuiDrawer-paper': {
          boxSizing: 'border-box',
          width: { xs: '100%', sm: width },
        },
      }}
      open={open}
      onClose={onClose}
    >
      <SafeArea top>
        <TitleAndClose
          title={title}
          onClose={onClose}
          leftAdornment={leftAdornment}
          rightAdornment={rightAdornment}
          titleVariant={titleVariant}
        />
      </SafeArea>
      {children}
    </MuiDrawer>
  );
};
